import React, { useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { Editor, EditorProvider } from '@tiptap/react';
import { extensions, buildTocFromContent, TocItem, scrollToTocItem } from './utils/EditorUtils';
import { useHelpContent } from '../../contexts/HelpContentContext';
import theme from '../../themes/baseTheme';

export const HelpPreview: React.FC = () => {
  const { content, isLoading, error } = useHelpContent();
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [editorDom, setEditorDom] = useState<HTMLElement | null>(null);

  const tocLookup = React.useMemo(() => {
    const map = new Map<string, TocItem[]>();
    tocItems.forEach((item) => {
      const key = item.text.trim().toLowerCase();
      if (!key) return;
      const arr = map.get(key) ?? [];
      arr[item.occurrence] = item;
      map.set(key, arr);
    });
    return map;
  }, [tocItems]);

  React.useEffect(() => {
    if (content) {
      setTocItems(buildTocFromContent(content as any));
    }
  }, [content]);

  React.useEffect(() => {
    setActiveItemId(tocItems[0]?.id ?? null);
  }, [tocItems]);

  const getListItemOwnText = (element: HTMLElement): string => {
    let text = '';
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || '';
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        !['OL', 'UL'].includes((node as HTMLElement).tagName)
      ) {
        text += (node as HTMLElement).textContent || '';
      }
    });
    return text.trim();
  };

  const getElementLabel = (el: HTMLElement): string => {
    if (el.tagName.toLowerCase() === 'li') {
      return getListItemOwnText(el);
    }
    return (el.textContent || '').trim();
  };

  const findScrollParent = (el: HTMLElement | null): HTMLElement | Window => {
    let current = el?.parentElement;

    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY || style.overflow;
      const isScrollable =
        ['auto', 'scroll', 'overlay'].includes(overflowY) &&
        current.scrollHeight > current.clientHeight;

      if (isScrollable) {
        return current;
      }

      current = current.parentElement;
    }

    return window;
  };

  React.useEffect(() => {
    if (!editorDom || typeof window === 'undefined') return;

    const scrollParent = findScrollParent(editorDom);
    const isWindowParent = scrollParent === window;

    const handleScroll = () => {
      const dom = editorDom;

      const parentRect = isWindowParent
        ? { top: 0 }
        : (scrollParent as HTMLElement).getBoundingClientRect();
      const viewportHeight = isWindowParent
        ? window.innerHeight
        : (scrollParent as HTMLElement).clientHeight;
      const reference = Math.max(0, viewportHeight * 0.15);

      const candidates: { id: string; top: number }[] = [];
      const nodes = dom.querySelectorAll('h1, h2, h3, h4, h5, h6, li');

      const seen = new Map<string, number>();
      nodes.forEach((node) => {
        const label = getElementLabel(node as HTMLElement).trim().toLowerCase();
        if (!label) return;
        const occurrence = seen.get(label) ?? 0;
        seen.set(label, occurrence + 1);
        const tocItem = tocLookup.get(label)?.[occurrence];
        if (!tocItem) return;

        const rect = (node as HTMLElement).getBoundingClientRect();
        const topWithinParent = rect.top - parentRect.top;
        candidates.push({ id: tocItem.id, top: topWithinParent });
      });

      if (!candidates.length) return;

      let bestAbove: { id: string; top: number } | null = null;
      let bestBelow: { id: string; top: number } | null = null;

      candidates.forEach((c) => {
        if (c.top <= reference) {
          if (!bestAbove || c.top > bestAbove.top) {
            bestAbove = c;
          }
        } else {
          if (!bestBelow || c.top < bestBelow.top) {
            bestBelow = c;
          }
        }
      });

      const nextActive = (bestAbove ?? bestBelow)?.id ?? null;
      setActiveItemId((prev) => (prev === nextActive ? prev : nextActive));
    };

    handleScroll(); // initial calculation

    const target = isWindowParent ? window : scrollParent;
    target.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [editorDom, tocLookup]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 200px)',
            width: '100%',
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading content...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 200px)',
            width: '100%',
            color: 'error.main',
          }}
        >
          Error loading content: {error}
        </Box>
      );
    }

    if (!content) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 200px)',
            width: '100%',
          }}
        >
          No content available
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* TOC sidebar */}
        <Paper
          elevation={1}
          sx={{
            width: '20%',
            mr: 2,
            bgcolor: 'grey.50',
            height: 'fit-content',
            position: 'sticky',
            top: '10.5%',
            display: 'flex',
            flexDirection: 'column',
            outline: `1px solid ${theme.palette.grey[50]}`,
          }}
        >
          {/* Fixed TOC header */}
          <Box
            sx={{
              p: 1.5,
              pl: 3,
              pb: 1,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              position: 'sticky',
              top: 0,
              bgcolor: 'grey.50',
              zIndex: 1,
            }}
          >
            <Typography variant="h6">Table of Contents</Typography>
          </Box>

          {/* Scrollable TOC content */}
          <Box
            sx={{
              p: 1,
              pt: 1,
              maxHeight: '80vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8',
              },
            }}
          >
            <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
              {tocItems.map((item) => (
                <li
                  key={item.id}
                  style={{
                    marginLeft: (item.level - 1) * 16,
                    cursor: 'pointer',
                    marginBottom: 2.25,
                    padding: '0.5px 4px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    backgroundColor:'transparent',
                  }}
                  onClick={() => {
                    setActiveItemId(item.id);
                    scrollToTocItem(editorInstance, item);
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)')
                  }
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <Typography
                    noWrap
                    variant={'body2'}
                    sx={{
                      fontWeight: item.level <= 2 ? 500 : 400,
                      color: activeItemId === item.id ? 'primary.main' : 'text.primary',
                      transition: 'color 0.2s',
                      py: 0.2,
                    }}
                  >
                    {item.text.substring(0, 28)}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        </Paper>

        {/* Content area */}
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            bgcolor: 'grey.50',
            border: 'none',
            '& .help-preview-content img': {
              display: 'block',
              margin: '32px auto',
            },
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.body1,
            '& .ProseMirror': {
              outline: `1px solid ${theme.palette.grey[50]}`,
              borderRadius: 0.5,
              paddingX: theme.spacing(1.5),
              paddingY: theme.spacing(0.5),
            },
          }}
        >
          <EditorProvider
            extensions={extensions}
            content={content}
            editable={false}
            editorProps={{
              attributes: {
                class: 'preview-editor',
              },
            }}
            onCreate={({ editor }) => {
              setEditorInstance(editor);
              setEditorDom(editor?.view?.dom as HTMLElement);
            }}
          />
        </Paper>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: 0.7,
        paddingBottom: 0.2,
        width: '100%',
      }}
    >
      {renderContent()}
    </Box>
  );
};
